import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { profileApi } from '@/api/profile'
import { useAuthStore } from '@/stores/auth.store'
import type { AppLocale } from '@/types'

interface Props {
  className?: string
  /** layout compatto: mostra solo la bandiera quando chiuso (per navbar strette) */
  compact?: boolean
}

// Bandiera (emoji) + nome nativo. Una sola riga per lingua nel menu a discesa.
const LANGS: { code: string; flag: string; name: string; rtl?: boolean }[] = [
  { code: 'it', flag: '🇮🇹', name: 'Italiano' },
  { code: 'en', flag: '🇬🇧', name: 'English' },
  { code: 'de', flag: '🇩🇪', name: 'Deutsch' },
  { code: 'es', flag: '🇪🇸', name: 'Español' },
  { code: 'fr', flag: '🇫🇷', name: 'Français' },
  { code: 'pt', flag: '🇵🇹', name: 'Português' },
  { code: 'nl', flag: '🇳🇱', name: 'Nederlands' },
  { code: 'fi', flag: '🇫🇮', name: 'Suomi' },
  { code: 'tr', flag: '🇹🇷', name: 'Türkçe' },
  { code: 'ja', flag: '🇯🇵', name: '日本語' },
  { code: 'zh', flag: '🇨🇳', name: '中文' },
  { code: 'ru', flag: '🇷🇺', name: 'Русский' },
  { code: 'id', flag: '🇮🇩', name: 'Bahasa Indonesia' },
  { code: 'ar', flag: '🇸🇦', name: 'العربية', rtl: true },
]

export function LanguageSwitcher({ className, compact }: Props) {
  const { i18n } = useTranslation()
  const isAuthenticated = useAuthStore(s => s.isAuthenticated)
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const current = LANGS.find(l => l.code === i18n.language) ?? LANGS.find(l => i18n.language?.startsWith(l.code)) ?? LANGS[0]

  // chiudi al click fuori / Esc
  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onClick); document.removeEventListener('keydown', onKey) }
  }, [open])

  const handleChange = (lang: string, rtl?: boolean) => {
    i18n.changeLanguage(lang)
    localStorage.setItem('edive-lang', lang)
    // Effetto sul DOM del documento in un handler: non e' una mutazione di stato React.
    // eslint-disable-next-line react-hooks/immutability
    document.documentElement.dir = rtl ? 'rtl' : 'ltr'
    // eslint-disable-next-line react-hooks/immutability
    document.documentElement.lang = lang
    setOpen(false)
    // Persist locale to profile when logged in. All 14 app languages are accepted
    // by the backend (Profile.locale), so the user's UI + communications language stays in sync.
    if (isAuthenticated && LANGS.some(l => l.code === lang)) {
      profileApi.updateMyProfile({ locale: lang as AppLocale }).catch(() => {})
    }
  }

  return (
    <div ref={ref} className={`relative ${className ?? ''}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex items-center gap-1.5 px-2 py-1 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-colors"
        title={current.name}
      >
        <span className="text-base leading-none">{current.flag}</span>
        {!compact && <span className="font-medium">{current.name}</span>}
        <ChevronDownIcon className={`w-3.5 h-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute right-0 z-50 mt-1 w-44 max-h-80 overflow-auto rounded-xl border border-gray-200 bg-white shadow-lg py-1"
        >
          {LANGS.map(({ code, flag, name, rtl }) => (
            <li key={code}>
              <button
                type="button"
                role="option"
                aria-selected={i18n.language === code}
                onClick={() => handleChange(code, rtl)}
                className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-sm text-left hover:bg-gray-100 ${
                  i18n.language === code ? 'bg-ocean-50 text-ocean-700 font-semibold' : 'text-gray-700'
                }`}
              >
                <span className="text-base leading-none">{flag}</span>
                <span className="flex-1">{name}</span>
                {i18n.language === code && <span className="text-ocean-500">✓</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
